import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, ChevronRight, Inbox, Loader2 } from 'lucide-react';
import type { FunnelStepMetrics } from '@dxp/schema';
import { getFunnel, getSessions, qk, type FunnelResponse, type FunnelSegmentFilter } from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import SegmentSecici, { type SegmentSecim } from '../components/SegmentSecici.js';
import { useRole } from '../components/AuthGate.js';
import FunnelChartContainer, { type FunnelModel } from '../components/FunnelChart.js';
import { useActivePid } from '../components/Layout.js';
import { formatDateTime, formatDuration, formatNumber, formatPct } from '../lib/format.js';

/**
 * §13.3 Funnel — otomatik keşfedilen huni.
 * Görsel dil ve Sankey çizimi referans DXP admin panelindeki `FunnelPage` ile birebirdir.
 */

/** Terk listesi panel tavanı — sunucu tavanının (§33.5) ALTINDA, o yüzden ayrıca söylenir. */
const LISTE_TAVANI = 50;

/** Oranı (0..1) tek ondalıklı yüzdeye çevirir — §94.1 biçimi. */
function yuzde1(oran: number): number {
  return Math.round(oran * 1000) / 10;
}

/* -------------------------------------------------------------------------- */
/* Veri → çizim modeli                                                         */
/* -------------------------------------------------------------------------- */

/**
 * §11.6 adım metriklerini Sankey modeline çevirir.
 *
 * İlk adım GİRİŞ düğümüdür (mavi); sonraki her adım omurgada bir düğümdür.
 * Bir adımın `exitCount`'u, önceki adıma girip bu adıma ULAŞAMAYAN oturumlardır —
 * huni metrikleri sıralı alt-dizi eşleşmesiyle hesaplandığı için bu fark doğrudan kayıptır.
 */
export function buildFunnelModel(
  steps: FunnelStepMetrics[],
  sources: { name: string; count: number }[],
): FunnelModel {
  if (steps.length === 0) {
    return { sources: [], entry: { label: 'Giriş', count: 0 }, steps: [], total: 0, success: 0 };
  }

  const first = steps[0] as FunnelStepMetrics;
  const total = first.entered;

  const chartSteps = steps.slice(1).map((step, i) => {
    const prev = steps[i] as FunnelStepMetrics; // slice(1) → i, orijinalde i. adım
    return {
      label: step.label,
      visitors: step.entered,
      /*
       * §94.1/2 — dönüşüm oranı TEK ONDALIKLA yazılır. `Math.round` ile tam sayıya
       * yuvarlamak, %42,1 ile %41,6'yı aynı gösteriyordu; dönem karşılaştırmasında
       * ("önceki %38,9") bu, gerçek bir farkı görünmez kılar (denetim bulgusu C5).
       */
      convRate: total > 0 ? yuzde1(step.entered / total) : 0,
      exitCount: Math.max(0, prev.entered - step.entered),
    };
  });

  const success = chartSteps.length > 0 ? (chartSteps[chartSteps.length - 1]?.visitors ?? 0) : total;

  return {
    sources: sources.filter((s) => s.count > 0),
    entry: { label: first.label, count: total },
    steps: chartSteps,
    total,
    success,
  };
}

/** Referans paneldeki `Switch` — trafik kaynakları açma/kapama. */
function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand' : 'bg-[#e5e7eb]'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Sayfa                                                                       */
/* -------------------------------------------------------------------------- */

export default function Funnel() {
  const pid = useActivePid();
  const role = useRole(pid);
  const [includeSources, setIncludeSources] = useState(true);
  const [activeStep, setActiveStep] = useState<number | null>(null);

  /*
   * §92.4 — seçim modeli: hazır kırılım STATE'te (eski davranış), kayıtlı segment
   * URL'de (`?segment=`) — sekme paylaşılabilir kalır. İkisi tek `SegmentSecim`
   * değerinde birleşir; API çağrısına yalnız biri gider (sunucu ikisini birden 400'ler).
   */
  const [params, setParams] = useSearchParams();
  const kayitliId = params.get('segment');
  const [hazirSecim, setHazirSecim] = useState<SegmentSecim>({ tur: 'tumu' });
  const secim: SegmentSecim = kayitliId !== null ? { tur: 'kayitli', id: kayitliId } : hazirSecim;

  function secimiDegistir(yeni: SegmentSecim): void {
    setActiveStep(null);
    if (yeni.tur === 'kayitli') {
      setHazirSecim({ tur: 'tumu' });
      const next = new URLSearchParams(params);
      next.set('segment', yeni.id);
      setParams(next, { replace: true });
      return;
    }
    if (kayitliId !== null) {
      const next = new URLSearchParams(params);
      next.delete('segment');
      setParams(next, { replace: true });
    }
    setHazirSecim(yeni);
  }

  const segment: FunnelSegmentFilter | undefined =
    secim.tur === 'hazir' ? { dimension: secim.dimension, value: secim.value } : undefined;
  const segmentId = secim.tur === 'kayitli' ? secim.id : undefined;

  const query = useQuery({
    queryKey: qk.funnel(pid, segment, segmentId, true),
    queryFn: ({ signal }) => getFunnel(pid, segment, signal, segmentId, true),
  });

  // Segment seçenekleri her zaman filtresiz yanıttan gelir (liste daralmasın).
  const base = useQuery({
    queryKey: qk.funnel(pid),
    queryFn: ({ signal }) => getFunnel(pid, undefined, signal),
  });

  /*
   * §13.3 — liste SEÇİLİ ADIMA aittir. Eskiden `step` hiç gönderilmiyordu: her
   * adım için aynı "dönüşmemiş oturumlar" listesi çiziliyor, başlıksa adımın adını
   * yazıyordu (denetim bulgusu A1). `activeStep` hem sorguya hem anahtara girer.
   */
  const abandoned = useQuery({
    queryKey: qk.sessions(pid, 'abandoned', segmentId, activeStep ?? undefined),
    queryFn: ({ signal }) => getSessions(pid, 'abandoned', signal, segmentId, activeStep ?? undefined),
    enabled: activeStep !== null,
  });

  const data: FunnelResponse | undefined = query.data;
  const steps = data?.steps ?? [];

  // §13.3 — trafik kaynakları: kanal segmentlerinin ilk adım oturumları.
  const sources = (base.data?.segments ?? [])
    .filter((s) => s.dimension === 'channel')
    .map((s) => ({ name: s.value, count: s.steps[0]?.entered ?? 0 }))
    .filter((s) => s.count > 0)
    .sort((a, b) => b.count - a.count);

  const model = buildFunnelModel(steps, includeSources ? sources : []);

  /*
   * §94 — adım kutusuna "önceki %X" satırı: önceki pencerenin GENEL metrikleri
   * adım SIRASIYLA eşlenir (desenler aynı funnel tanımından gelir). Önceki
   * pencere boşsa (`previous_steps: null`) hiçbir kıyas çizilmez (§94.2).
   */
  const oncekiAdimlar = data?.previous_steps ?? null;
  if (oncekiAdimlar !== null && oncekiAdimlar.length === steps.length) {
    const oncekiToplam = oncekiAdimlar[0]?.entered ?? 0;
    model.steps = model.steps.map((adim, i) => {
      const onceki = oncekiAdimlar[i + 1]; // model.steps girişten SONRAKİ adımlardır
      return {
        ...adim,
        prevConvRate:
          onceki !== undefined && oncekiToplam > 0
            ? yuzde1(onceki.entered / oncekiToplam)
            : null,
      };
    });
  }
  const convRate = model.total > 0 ? yuzde1(model.success / model.total) : 0;
  const dropOff = Math.max(0, model.total - model.success);

  // §92.4 "Hazır" grubu — filtresiz yanıtın cihaz/kanal tekilleri (liste daralmasın).
  const hazirSecenekler = (base.data?.segments ?? []).map((s) => ({
    dimension: s.dimension,
    value: s.value,
  }));

  const exitRows = model.steps.filter((s) => s.exitCount > 0).length;
  const chartHeight = Math.max(520, 280 + exitRows * 60);

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="funnel-sayfasi">
      {/* başlık */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Funnel</h1>
          <BilgiDugmesi testId="funnel-bilgi">Huni, otomatik keşfedilen hedeften geriye doğru türetilir (§11.6). Hiçbir adım elle tanımlanmaz.</BilgiDugmesi>
        </div>
        {data?.funnel?.computed_at ? (
          <span className="text-[12px] text-[#9ca3af]">
            Hesaplandı: {formatDateTime(data.funnel.computed_at)}
          </span>
        ) : null}
      </div>

      {/* filtreler */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[#e5e7eb] bg-white px-6 py-3">
        <SegmentSecici
          pid={pid}
          role={role}
          secim={secim}
          onChange={secimiDegistir}
          hazirSecenekler={hazirSecenekler}
        />
        <div className="ml-2 flex items-center gap-2">
          <span className="text-[13px] text-[#374151]">Trafik Kaynakları</span>
          <Switch checked={includeSources} onChange={setIncludeSources} />
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {query.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Toplam Oturum', value: formatNumber(model.total) },
            { label: 'Dönüşen', value: formatNumber(model.success) },
            { label: 'Dönüşüm Oranı', value: formatPct(convRate) },
            { label: 'Terk', value: formatNumber(dropOff) },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{s.value}</span>
            </div>
          ))
        )}
      </div>

      {/* grafik */}
      <div className="px-6 py-8">
        <div
          className="relative overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white"
          style={{ height: chartHeight }}
        >
          {query.isLoading ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90">
              <Loader2 className="h-7 w-7 animate-spin text-brand" />
              <p className="text-[13px] text-[#6b7280]">Veri yükleniyor…</p>
            </div>
          ) : null}

          {/*
            §91.5/3 — HATA, VERİ YOKLUĞU DEĞİLDİR. Sayfa `isError`ı hiç okumuyordu:
            uç düştüğünde ekran "Hedef henüz keşfedilmedi" diyordu, yani kullanıcıya
            "veriniz yok" yalanını söylüyordu (denetim bulgusu B6).
          */}
          {!query.isLoading && query.isError ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
              data-testid="funnel-hata"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <p className="text-[15px] font-semibold text-[#374151]">Funnel okunamadı</p>
              <p className="text-[13px] text-[#9ca3af]">
                Sunucuya ulaşılamıyor — bu, hedefin keşfedilmediği anlamına gelmez.
              </p>
              <button type="button" className="btn-secondary" onClick={() => void query.refetch()}>
                Yeniden dene
              </button>
            </div>
          ) : null}

          {!query.isLoading && !query.isError && model.total === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f9fafb]">
                <Inbox className="h-6 w-6 text-[#9ca3af]" />
              </div>
              <p className="text-[15px] font-semibold text-[#374151]">Hedef henüz keşfedilmedi</p>
              <p className="text-[13px] text-[#9ca3af]">
                Analiz 5 dakikada bir çalışır — trafik biriktikçe adımlar burada belirir
              </p>
            </div>
          ) : null}

          {!query.isLoading && !query.isError && model.total > 0 ? (
            <FunnelChartContainer data={model} />
          ) : null}
        </div>
      </div>

      {/* açıklama */}
      <div className="flex flex-wrap items-center gap-6 px-6 pb-6">
        {[
          { color: '#86efac', label: 'Dönüşüm akışı' },
          { color: '#fca5a5', label: 'Çıkış / Terk' },
          { color: '#d1d5db', label: 'Trafik kaynağı' },
          { color: '#3b82f6', label: 'Giriş adımı' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm" style={{ background: l.color }} />
            <span className="text-[12px] text-[#6b7280]">{l.label}</span>
          </div>
        ))}
      </div>

      {/* adım tablosu — tıklayınca o adımda terk edenler açılır (§13.3) */}
      {steps.length > 0 ? (
        <div className="px-6 pb-10">
          {/*
            AÇIK YÖNERGE. §13.3'ün "adım tıklanınca o adımda terk eden oturum
            listesi" davranışı çalışıyordu ama kullanıcıya HİÇBİR yerde
            söylenmiyordu; satırların tıklanabilirliği yalnız imleç üstüne gelince
            belli oluyordu (2026-08-18 keşfedilebilirlik taraması, D1 imzası).
          */}
          {/*
            DİKKAT — testid ÖNEKİ: `funnel-adim-*` deseni adım KUTULARINI saymak
            için kullanılıyor (`e2e/dashboard.spec.ts` `[data-testid^="funnel-adim-"]`).
            Bu yönerge ilk yazımda `funnel-adim-yonerge` adını almıştı ve o sayıma
            KARIŞTI: `steps.first()` paragrafı seçiyor, tıklama hiçbir şey yapmıyor
            ve "adımında terk edenler" paneli hiç açılmıyordu. Ad bu yüzden önekin
            DIŞINDA.
          */}
          <p className="mb-2 text-[12px] text-[#6b7280]" data-testid="funnel-secim-yonergesi">
            Bir adıma tıklayın — o adımı görüp sonrakine geçmeyen oturumlar aşağıda listelenir.
          </p>
          <div className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white">
            <table className="w-full text-[13px]">
              <thead className="table-head">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Adım</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Desen</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Giren</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Geçen</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Kayıp</th>
                  {/* Ok sütunu — satırın tıklanabilir olduğunu DURGUN hâlde söyler. */}
                  <th className="w-8 px-2 py-2.5" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {steps.map((step, index) => {
                  const isLast = index === steps.length - 1;
                  const active = activeStep === index;
                  return (
                    <tr
                      key={`${step.pattern}-${index}`}
                      data-testid={`funnel-adim-${index}`}
                      onClick={() => setActiveStep(active ? null : index)}
                      className={`cursor-pointer border-t border-[#f3f4f6] transition-colors ${
                        active ? 'bg-[#fff4ec]' : 'hover:bg-[#fafafa]'
                      }`}
                    >
                      <td className="px-4 py-2.5 font-medium text-[#111827]">
                        {index + 1}. {step.label}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-[#9ca3af]">
                        {step.pattern}
                      </td>
                      <td className="num px-4 py-2.5 text-right text-[#374151]">
                        {formatNumber(step.entered)}
                      </td>
                      <td className="num px-4 py-2.5 text-right text-[#374151]">
                        {formatNumber(step.passed)}
                      </td>
                      <td
                        data-testid={`funnel-drop-${index}`}
                        className={`num px-4 py-2.5 text-right font-semibold ${
                          isLast ? 'text-emerald-600' : step.drop_pct >= 40 ? 'text-brand' : 'text-[#374151]'
                        }`}
                      >
                        {isLast ? 'hedef' : `%${step.drop_pct.toFixed(1)}`}
                      </td>
                      {/*
                        KEŞFEDİLEBİLİRLİK (2026-08-18 taraması, D1'in aynısı):
                        satırlar tıklanabilirdi ama DURGUN hâlde hiçbir işaret
                        taşımıyordu — ne ikon, ne yönerge; yalnız hover'da zemin
                        değişiyordu. Yani M55'te düzelttiğim "o adımda terk edenler"
                        listesine ulaşan yol, kullanıcı için görünmezdi.
                      */}
                      <td className="w-8 px-2 py-2.5 text-right">
                        <ChevronRight
                          size={14}
                          aria-hidden="true"
                          className={active ? 'text-brand' : 'text-[#d1d5db]'}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {activeStep !== null ? (
            <div className="mt-4 overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white">
              <div className="border-b border-[#e5e7eb] px-4 py-3">
                <p className="text-[13px] font-semibold text-[#111827]">
                  &ldquo;{steps[activeStep]?.label ?? ''}&rdquo; adımında terk edenler
                </p>
                <p className="text-[12px] text-[#9ca3af]">
                  Bu adımı gören, sonraki adıma GEÇMEYEN ve dönüşmemiş oturumlar
                </p>
                {/*
                  §33.5 — liste İKİ KEZ kırpılabiliyordu ve ikisi de sessizdi:
                  sunucu tavanı (`truncated`) okunmuyordu, üstüne `.slice(0,50)`
                  uygulanıyordu (denetim bulgusu B3). Artık ikisi de söylenir.
                */}
                {abandoned.data !== undefined &&
                (abandoned.data.truncated || abandoned.data.items.length > LISTE_TAVANI) ? (
                  <p
                    data-testid="funnel-liste-kirpildi"
                    className="mt-1 inline-block rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
                  >
                    İlk {formatNumber(Math.min(LISTE_TAVANI, abandoned.data.items.length))} oturum
                    gösteriliyor
                    {abandoned.data.truncated
                      ? ` — sunucu da bu uçta en çok ${formatNumber(abandoned.data.limit)} satır döndürür`
                      : ''}
                  </p>
                ) : null}
              </div>
              <table className="w-full text-[13px]">
                <thead className="table-head">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Oturum</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Başlangıç</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Süre</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Cihaz</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Kanal</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Sayfa</th>
                  </tr>
                </thead>
                <tbody>
                  {(abandoned.data?.items ?? []).slice(0, LISTE_TAVANI).map((r) => (
                    <tr key={r.session_id} className="border-t border-[#f3f4f6] hover:bg-[#fafafa]">
                      <td className="px-4 py-2.5 font-mono text-[12px]">
                        {r.session_id.slice(0, 10)}
                      </td>
                      <td className="px-4 py-2.5 text-[#374151]">{formatDateTime(r.started_at)}</td>
                      <td className="px-4 py-2.5 text-[#374151]">{formatDuration(r.duration_ms)}</td>
                      <td className="px-4 py-2.5 text-[#374151]">{r.device}</td>
                      <td className="px-4 py-2.5 text-[#374151]">{r.channel}</td>
                      <td className="num px-4 py-2.5 text-right text-[#374151]">
                        {formatNumber(r.page_count)}
                      </td>
                    </tr>
                  ))}
                  {abandoned.isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-[#9ca3af]">
                        Yükleniyor…
                      </td>
                    </tr>
                  ) : null}
                  {!abandoned.isLoading && (abandoned.data?.items ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-[#9ca3af]">
                        Bu adımda terk eden oturum bulunamadı
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
